var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { BufferEventType } from '../../types/index';
import { SpaceInfoWrapper } from '../SpaceDetails/EventSpaceWrapper';
import { Flex, theme, Typography } from '@robinpowered/ui-kit';
import { Button } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import styled from '@emotion/styled';
import { TimeInput } from './TimeInput';
import { useBuffers } from './hooks/useBuffers';
import { convertFromISODuration } from '../../utils/durationUtils';
import { useTranslation } from 'react-i18next';
import { useSpaceDetailsContext } from '../SpaceDetails/SpaceDetailsContext';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
import { AddSectionButton } from '../common/AddSectionButton';
import { useApprovedRequestFieldsLocked } from '../../hooks/useApprovedRequestFieldsLocked';
/* Max time in hours for a buffer 5 hours */
var MAX_BUFFER_INPUT_HOURS = 5;
/* Max time in minutes for a buffer */
var MAX_BUFFER_INPUT_MINUTES = MAX_BUFFER_INPUT_HOURS * 60;
/* 15 minutes is the default buffer duration */
var DEFAULT_BUFFER_VALUE = 15;
export var Buffers = function (_a) {
    var _b;
    var spaceId = _a.spaceId;
    var t = useTranslation('meetingRooms').t;
    var _c = useSpaceDetailsContext(), requestOnlySpace = _c.requestOnlySpace, initialEvent = _c.initialEvent, onTrackBufferEvent = _c.onTrackBufferEvent;
    var spacesWithConflicts = useEventComposerGlobalContext().spacesWithConflicts;
    var token = theme.useToken().token;
    var _d = __read(useWatch({
        name: ['isAllDayEvent', 'spaceBuffers', 'recurrenceRule'],
    }), 3), isAllDayEvent = _d[0], spaceBuffers = _d[1], recurrenceRule = _d[2];
    var _e = useBuffers({
        spaceBuffers: spaceBuffers,
        spaceId: spaceId,
        onTrackBufferEvent: onTrackBufferEvent,
    }), handleUpdateBuffers = _e.handleUpdateBuffers, onRemoveBuffer = _e.onRemoveBuffer, onAddBuffer = _e.onAddBuffer, showBuffers = _e.showBuffers;
    var canEditBuffers = !useApprovedRequestFieldsLocked(requestOnlySpace, (_b = initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.reservationApproval) === null || _b === void 0 ? void 0 : _b.approved);
    useEffect(function () {
        if (isAllDayEvent || recurrenceRule) {
            if (spaceBuffers)
                onRemoveBuffer();
        }
    }, [isAllDayEvent, recurrenceRule, spaceBuffers, onRemoveBuffer]);
    var getBufferInitialValue = useCallback(function (bufferValue) {
        var _a;
        if (!bufferValue) {
            return DEFAULT_BUFFER_VALUE;
        }
        return ((_a = convertFromISODuration(bufferValue || 'PT15M', 'minutes')) !== null && _a !== void 0 ? _a : DEFAULT_BUFFER_VALUE);
    }, []);
    var beforeEventInitialValue = useMemo(function () { var _a; return getBufferInitialValue((_a = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _a === void 0 ? void 0 : _a.beforeEvent); }, [spaceBuffers, spaceId, getBufferInitialValue]);
    var afterEventInitialValue = useMemo(function () { var _a; return getBufferInitialValue((_a = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _a === void 0 ? void 0 : _a.afterEvent); }, [spaceBuffers, spaceId, getBufferInitialValue]);
    var bufferHasConflict = useMemo(function () { var _a; return (_a = spacesWithConflicts === null || spacesWithConflicts === void 0 ? void 0 : spacesWithConflicts[spaceId]) === null || _a === void 0 ? void 0 : _a.spaceBuffers; }, [spacesWithConflicts, spaceId]);
    var prevBufferHasConflictRef = useRef(false);
    // Track when buffer conflict is detected (transitions from false to true)
    useEffect(function () {
        var _a, _b;
        var prevConflict = prevBufferHasConflictRef.current;
        var currentConflict = (bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.before) || (bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.after);
        if (!prevConflict && currentConflict && onTrackBufferEvent) {
            onTrackBufferEvent(BufferEventType.DETECTED_BUFFER_CONFLICT, {
                spaceId: spaceId,
                beforeDuration: (_a = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _a === void 0 ? void 0 : _a.beforeEvent,
                afterDuration: (_b = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _b === void 0 ? void 0 : _b.afterEvent,
            });
        }
        prevBufferHasConflictRef.current = !!currentConflict;
    }, [bufferHasConflict, spaceId, spaceBuffers, onTrackBufferEvent]);
    if (!canEditBuffers && !showBuffers) {
        return null;
    }
    return (_jsx(SpaceInfoWrapper
    // Divide the buffer block from the service requests above it once a
    // buffer exists; the empty "+ Add buffer" state stays borderless.
    , { 
        // Divide the buffer block from the service requests above it once a
        // buffer exists; the empty "+ Add buffer" state stays borderless.
        showBorder: showBuffers, style: __assign({ backgroundColor: 'white' }, (showBuffers ? {} : { paddingTop: '0px' })), children: showBuffers ? (_jsxs(Flex, { style: { width: '100%' }, gap: "4px", vertical: true, children: [_jsxs(Flex, { align: "center", justify: "space-between", style: { width: '100%' }, children: [_jsx(Typography.Text, { style: { color: token.colorTextLabel }, children: t('buffers.buffer') }), canEditBuffers && (_jsx(Button, { "aria-label": t('buffers.remove_buffer'), color: "danger", variant: "text", onClick: function () {
                                var _a, _b;
                                if (onTrackBufferEvent) {
                                    onTrackBufferEvent(BufferEventType.REMOVED_BUFFER, {
                                        spaceId: spaceId,
                                        beforeDuration: (_a = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _a === void 0 ? void 0 : _a.beforeEvent,
                                        afterDuration: (_b = spaceBuffers === null || spaceBuffers === void 0 ? void 0 : spaceBuffers[spaceId]) === null || _b === void 0 ? void 0 : _b.afterEvent,
                                    });
                                }
                                onRemoveBuffer();
                            }, icon: _jsx(FontAwesomeIcon, { icon: faTrash, size: "xs" }) }))] }), _jsxs(Flex, { style: { width: '100%' }, justify: "space-between", gap: "16px", children: [_jsxs(BufferSelector, { children: [_jsx(Typography.Text, { style: { color: token.colorTextLabel }, children: t('buffers.before_event') }), _jsx(TimeInput, { maxHour: MAX_BUFFER_INPUT_HOURS, maxMinutes: MAX_BUFFER_INPUT_MINUTES, initialValue: beforeEventInitialValue, disabled: !canEditBuffers, onChange: function (value, timeType) {
                                        handleUpdateBuffers(value, 'beforeEvent', timeType);
                                    }, error: bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.before }, beforeEventInitialValue)] }), _jsxs(BufferSelector, { children: [_jsx(Typography.Text, { style: { color: token.colorTextLabel }, children: t('buffers.after_event') }), _jsx(TimeInput, { maxHour: MAX_BUFFER_INPUT_HOURS, maxMinutes: MAX_BUFFER_INPUT_MINUTES, initialValue: afterEventInitialValue, disabled: !canEditBuffers, onChange: function (value, timeType) {
                                        handleUpdateBuffers(value, 'afterEvent', timeType);
                                    }, error: bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.after }, afterEventInitialValue)] })] }), canEditBuffers &&
                    ((bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.before) || (bufferHasConflict === null || bufferHasConflict === void 0 ? void 0 : bufferHasConflict.after)) && (_jsx(ErrorWrapper, { children: _jsx(ErrorText, { style: { color: token.colorError }, children: bufferHasConflict.before && bufferHasConflict.after
                            ? t('buffers.buffer_conflict')
                            : t("buffers.buffer_conflict_".concat(bufferHasConflict.before ? 'before' : 'after')) }) }))] })) : canEditBuffers ? (_jsx(AddSectionButton, { label: t('buffers.add_buffer'), onClick: function () {
                if (onTrackBufferEvent) {
                    onTrackBufferEvent(BufferEventType.ADDED_BUFFER, {
                        spaceId: spaceId,
                    });
                }
                onAddBuffer();
            } })) : null }));
};
var BufferSelector = styled(Flex)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  width: 100%;\n  align-items: center;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: flex-start;\n"], ["\n  width: 100%;\n  align-items: center;\n  flex-direction: column;\n  justify-content: space-between;\n  align-items: flex-start;\n"])));
var ErrorWrapper = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  margin-top: 8px;\n"], ["\n  display: flex;\n  align-items: center;\n  margin-top: 8px;\n"])));
var ErrorText = styled(Typography.Text)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: block;\n  margin-left: 4px;\n"], ["\n  display: block;\n  margin-left: 4px;\n"])));
var templateObject_1, templateObject_2, templateObject_3;
